/**
 * Hariom ERP - Dashboard Page
 * Main overview with KPIs, charts, alerts, and quick stats
 * First screen after login - gives founder/director a complete picture
 */
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import {
  TrendingUp, Users, ArrowUpRight, ArrowDownRight, Target, Zap,
  ShoppingCart, Gem, Receipt, Boxes, Landmark
} from 'lucide-react'
import { useForecastStore } from '../../store/forecastStore'
import { useSalesBookStore, invoiceTotal } from '../../store/salesBookStore'
import { useDiamondPurchaseStore, purchaseTotal } from '../../store/diamondPurchaseStore'
import { usePurchaseStore } from '../../store/purchaseStore'

// Compact Indian-currency format for KPI cards (₹ Cr / ₹ L / ₹).
const inrCompact = (n: number) => {
  if (n >= 1e7) return `₹ ${(n / 1e7).toFixed(2)} Cr`
  if (n >= 1e5) return `₹ ${(n / 1e5).toFixed(2)} L`
  return `₹ ${Math.round(n).toLocaleString('en-IN')}`
}

// Product mix has no live source yet (no Sales store), so it stays static.
const productMixData = [
  { name: 'Epoxy TMT',       value: 45, color: '#C41E3A' },
  { name: 'Epoxy Dowel',     value: 25, color: '#2E7D32' },
  { name: 'Fusion TMT',      value: 15, color: '#1e40af' },
  { name: 'Fusion Dowel',    value: 10, color: '#7c3aed' },
  { name: 'Other',           value: 5,  color: '#94a3b8' },
]

// Months shown on the dashboard trend chart (real data is FY Mar→Aug).
const TREND_MONTHS: [string, string][] = [
  ['MARCH', 'Mar'], ['APR', 'Apr'], ['MAY', 'May'],
  ['JUNE', 'Jun'], ['JULY', 'Jul'], ['AUG', 'Aug'],
]

// ===== COMPONENT =====
const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  // Live demand-forecast data (the real 110 contractors).
  const { contractors } = useForecastStore()
  // Live data from the operating modules (all persisted / shared).
  const salesInvoices = useSalesBookStore(s => s.invoices)
  const salesParties = useSalesBookStore(s => s.parties)
  const dpOrders = useDiamondPurchaseStore(s => s.orders)
  const dpSuppliers = useDiamondPurchaseStore(s => s.suppliers)
  const purchaseOrders = usePurchaseStore(s => s.orders)

  // ----- Module KPIs (JP Gems Sales / Diamond Purchase + Hariom Purchase) -----
  const salesStats = useMemo(() => {
    const open = salesInvoices.filter(i => i.status === 'open')
    return { openDues: open.reduce((s, i) => s + invoiceTotal(i), 0), openCount: open.length, parties: salesParties.length }
  }, [salesInvoices, salesParties])
  const dpStats = useMemo(() => {
    const open = dpOrders.filter(o => o.status === 'open')
    return { value: dpOrders.reduce((s, o) => s + purchaseTotal(o), 0), openCount: open.length, total: dpOrders.length }
  }, [dpOrders])
  const purchaseStats = useMemo(() => ({
    value: purchaseOrders.reduce((s, o) => s + (o.total || 0), 0), count: purchaseOrders.length,
  }), [purchaseOrders])

  // ----- Derived, REAL numbers from the forecast store -----
  const totalForecastYTD = useMemo(
    () => contractors.reduce((s, c) => s + c.totalForecast, 0), [contractors])
  const totalActualYTD = useMemo(
    () => contractors.reduce((s, c) => s + c.totalActual, 0), [contractors])
  const activeCount = contractors.filter(c => c.status === 'active').length

  // Monthly forecast vs actual for the area chart.
  const monthlyTrendData = useMemo(() =>
    TREND_MONTHS.map(([key, label]) => ({
      month: label,
      forecast: contractors.reduce((s, c) => s + (c.monthlyData.find(m => m.month === key)?.forecast || 0), 0),
      actual:   contractors.reduce((s, c) => s + (c.monthlyData.find(m => m.month === key)?.actual || 0), 0),
    })), [contractors])

  // Top contractors by shortfall (forecast − actual, biggest first).
  const topContractors = useMemo(() => contractors
    .filter(c => c.totalForecast > 0)
    .map(c => ({ name: c.name, forecast: c.totalForecast, actual: c.totalActual, diff: c.totalForecast - c.totalActual }))
    .sort((a, b) => b.diff - a.diff)
    .slice(0, 5), [contractors])

  // Action-required alerts derived from real data.
  const alerts = useMemo(() => {
    const list: { id: string; type: string; icon: string; msg: string; link: string }[] = []
    contractors.filter(c => c.status === 'need_to_talk').slice(0, 3).forEach((c, i) =>
      list.push({ id: `nt-${i}`, type: 'danger', icon: '🔴',
        msg: `${c.name}: NEED TO TALK — ${Math.abs(c.totalDiff)}T gap vs commitment`, link: '/forecast' }))
    topContractors.filter(c => c.diff > 0).slice(0, 2).forEach((c, i) =>
      list.push({ id: `gap-${i}`, type: 'warning', icon: '🟡',
        msg: `${c.name}: ${c.diff}T below commitment — follow up`, link: '/forecast' }))
    if (list.length === 0)
      list.push({ id: 'ok', type: 'info', icon: '🔵', msg: 'All contractors are on track — no follow-ups pending', link: '/forecast' })
    return list
  }, [contractors, topContractors])

  // KPI cards — only metrics backed by REAL data in the app are shown.
  // (Production / Revenue / Receivables placeholders removed until those
  // modules have their own data stores.)
  const kpis = [
    { label: 'Total Forecast (FY26)', value: `${totalForecastYTD.toLocaleString()} T`, change: +12.5, icon: Target,     color: 'bg-primary-50 text-primary-600', borderColor: 'border-l-primary-500' },
    { label: 'Actual Offtake (YTD)',  value: `${totalActualYTD.toLocaleString()} T`,   change: +8.3,  icon: TrendingUp, color: 'bg-green-50 text-green-600',     borderColor: 'border-l-green-500' },
    { label: 'Active Contractors',    value: `${activeCount}`, change: +3, icon: Users,   color: 'bg-teal-50 text-teal-600',       borderColor: 'border-l-teal-500' },
  ]

  // Module KPIs — live from the Sales Order, Diamond Purchase & Purchase pages.
  // Each card is clickable and jumps to the page the sales/purchase team works on.
  const moduleKpis = [
    { label: 'Sales Open Dues',      value: inrCompact(salesStats.openDues), sub: `${salesStats.openCount} open invoice${salesStats.openCount === 1 ? '' : 's'}`, icon: Receipt,      color: 'bg-primary-50 text-primary-600', borderColor: 'border-l-primary-500', link: '/sales-order' },
    { label: 'Sales Parties',        value: `${salesStats.parties}`,          sub: 'active clients',                                                             icon: Users,        color: 'bg-rose-50 text-rose-600',       borderColor: 'border-l-rose-500',    link: '/sales-order' },
    { label: 'Diamond Purchase',     value: inrCompact(dpStats.value),        sub: `${dpStats.total} PO${dpStats.total === 1 ? '' : 's'} · ${dpStats.openCount} open`, icon: Gem,          color: 'bg-indigo-50 text-indigo-600',   borderColor: 'border-l-indigo-500',  link: '/diamond-purchase' },
    { label: 'Purchase Orders',      value: `${purchaseStats.count}`,         sub: 'goods POs generated',                                                        icon: ShoppingCart, color: 'bg-amber-50 text-amber-600',     borderColor: 'border-l-amber-500',   link: '/purchase' },
    { label: 'Purchase Value',       value: inrCompact(purchaseStats.value),  sub: 'total PO spend',                                                             icon: Landmark,     color: 'bg-emerald-50 text-emerald-600', borderColor: 'border-l-emerald-500', link: '/purchase' },
    { label: 'Diamond Suppliers',    value: `${dpSuppliers.length}`,          sub: 'in the book',                                                                icon: Boxes,        color: 'bg-sky-50 text-sky-600',         borderColor: 'border-l-sky-500',     link: '/diamond-purchase' },
  ]

  return (
    <div className="space-y-6">

      {/* ===== PAGE HEADER ===== */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Good Morning, Director 👋</h1>
          <p className="page-subtitle">
            Hariom EpoxyShield ERP — FY 2026 Overview
          </p>
        </div>
        {/* Last updated indicator */}
        <div className="hidden sm:flex items-center gap-2 text-xs text-steel-500 bg-steel-100 px-3 py-1.5 rounded-full">
          <Zap size={12} className="text-yellow-500" />
          Live data · Updated just now
        </div>
      </div>

      {/* ===== KPI CARDS ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon
          const isPositive = kpi.change > 0
          return (
            <div
              key={kpi.label}
              className={`card border-l-4 ${kpi.borderColor} py-4 px-4`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${kpi.color}`}>
                  <Icon size={16} />
                </div>
                {/* Change indicator */}
                <span className={`flex items-center text-xs font-medium ${
                  isPositive ? 'text-green-600' : 'text-red-500'
                }`}>
                  {isPositive
                    ? <ArrowUpRight size={12} />
                    : <ArrowDownRight size={12} />
                  }
                  {Math.abs(kpi.change)}%
                </span>
              </div>
              <p className="text-xl font-bold text-steel-900 leading-tight">{kpi.value}</p>
              <p className="text-xs text-steel-500 mt-1 leading-tight">{kpi.label}</p>
            </div>
          )
        })}
      </div>

      {/* ===== JP GEMS / PURCHASE MODULE KPIs (live) ===== */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-steel-700">Business Operations</h3>
          <span className="text-[11px] text-steel-400">JP Gems Sales · Diamond Purchase · Hariom Purchase — live</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {moduleKpis.map((kpi) => {
            const Icon = kpi.icon
            return (
              <button
                key={kpi.label}
                onClick={() => navigate(kpi.link)}
                className={`card border-l-4 ${kpi.borderColor} py-4 px-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all`}
              >
                <div className={`inline-flex p-2 rounded-lg mb-2 ${kpi.color}`}>
                  <Icon size={16} />
                </div>
                <p className="text-lg font-bold text-steel-900 leading-tight truncate" title={kpi.value}>{kpi.value}</p>
                <p className="text-xs font-medium text-steel-600 mt-1 leading-tight">{kpi.label}</p>
                <p className="text-[11px] text-steel-400 leading-tight">{kpi.sub}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* ===== CHARTS ROW ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Monthly Forecast vs Actual (spans 2/3 width) */}
        <div className="card xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-steel-800">Forecast vs Actual (Tons)</h3>
              <p className="text-xs text-steel-500">Monthly demand commitment vs actual offtake</p>
            </div>
            <select className="text-xs border border-steel-200 rounded-lg px-2 py-1 text-steel-600">
              <option>FY 2026</option>
              <option>FY 2025</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={monthlyTrendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                {/* Gradient fills for area chart */}
                <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C41E3A" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#C41E3A" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2E7D32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(val: number) => [`${val.toLocaleString()} T`]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="forecast" name="Forecast" stroke="#C41E3A"
                    strokeWidth={2} fill="url(#forecastGrad)" />
              <Area type="monotone" dataKey="actual" name="Actual" stroke="#2E7D32"
                    strokeWidth={2} fill="url(#actualGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Product Mix Pie Chart */}
        <div className="card">
          <h3 className="font-semibold text-steel-800 mb-1">Product Mix</h3>
          <p className="text-xs text-steel-500 mb-4">Sales volume by product type</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={productMixData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="#ffffff"
                strokeWidth={2}
              >
                {productMixData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(val) => [`${val}%`]} />
            </PieChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="space-y-1 mt-2">
            {productMixData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                  <span className="text-steel-600">{item.name}</span>
                </div>
                <span className="font-semibold text-steel-800">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== BOTTOM ROW: Alerts + Top Contractors ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* Alerts Panel */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-steel-800">⚡ Action Required</h3>
            <span className="badge-danger">{alerts.filter(a => a.type === 'danger').length} Critical</span>
          </div>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                onClick={() => navigate(alert.link)}
                className={`flex items-start gap-3 p-3 rounded-lg text-sm border-l-3 cursor-pointer hover:brightness-95 transition ${
                  alert.type === 'danger'  ? 'bg-red-50 border-l-4 border-red-400' :
                  alert.type === 'warning' ? 'bg-yellow-50 border-l-4 border-yellow-400' :
                                             'bg-blue-50 border-l-4 border-blue-400'
                }`}
              >
                <span className="flex-shrink-0 text-base leading-tight">{alert.icon}</span>
                <p className="text-steel-700 text-xs leading-relaxed">{alert.msg}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Contractors by Gap */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-steel-800">Top Contractors by Gap</h3>
            <p className="text-xs text-steel-500">Forecast vs Actual difference</p>
          </div>
          <div className="space-y-3">
            {topContractors.map((c, i) => {
              const pct = Math.max(0, Math.round((c.actual / c.forecast) * 100))
              return (
                <div key={c.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-steel-700 text-xs">{c.name}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-steel-500">{c.actual}T / {c.forecast}T</span>
                      <span className="diff-negative">-{c.diff}T</span>
                    </div>
                  </div>
                  {/* Progress bar showing fulfillment % */}
                  <div className="w-full bg-steel-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-steel-400 mt-0.5 text-right">{pct}% fulfilled</p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

    </div>
  )
}

export default Dashboard
